import classnames from 'classnames'
import React, { memo, useCallback, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { ProductCardTag } from '@src/components/product-card/product-card.types'
import { ProductTags } from '@src/components/product-card/product-image-section/product-tags'
import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import Directory from '@src/quince-site-common/src/domains/Directory'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import usePDPModelSizeTagExperiment from '@src/quince-site-common/src/hooks/usePDPModelSizeTagExperiment'
import usePDPTagVisibility from '@src/quince-site-common/src/hooks/usePDPTagVisibility'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { TAG_LOCATION, TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { getVideoInfoForColor } from '@src/quince-site-pdp/src/molecules/productFormWrapper/utils'
import {
  DISABLE_PARTIAL_V2_DESIGN,
  useNewPdpDesignV2Bucket,
} from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpDesignV2'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

import Heart from '../../../../quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'
import { BreadCrumb } from '../breadCrumb'
import Icon from '../icon/Icon'
import { SliderImages, SliderImagesProps } from '../sliderImages'

import styles from './sliderImageText.module.scss'
import stylesV2 from './sliderImageTextV2.module.scss'

export interface SliderImageWithTextProps {
  className?: string
  sliderImageProps: SliderImagesProps
  isWishListed?: boolean
  hideWishList?: boolean
  productTags?: ProductTag[]
  onWishListToggle?: (e: React.MouseEvent<HTMLElement | SVGElement>) => void
  directory: Directory
  getTopLeftIconSVG?: ({ index }: { index: number }) => ((params: any) => JSX.Element) | null
  hideBreadCrumb?: boolean
  breadCrumbListClass?: string
  breadCrumbContainerHeaderClass?: string
  variants?: any[]
  product?: Product
  mediaTags?: (ProductCardTag | null)[]
  isBundle?: boolean
  selectedOptions?: ProductVariantOption[]
}

const SliderImageWithText = ({
  className,
  sliderImageProps,
  isWishListed,
  onWishListToggle,
  productTags,
  directory,
  hideWishList,
  getTopLeftIconSVG,
  hideBreadCrumb,
  breadCrumbListClass,
  breadCrumbContainerHeaderClass,
  variants,
  product,
  isBundle,
  mediaTags,
  selectedOptions,
}: SliderImageWithTextProps) => {
  const deviceInfo = useDeviceInfo()
  const { isTagVisible } = usePDPTagVisibility()
  const newPdpDesignV2Bucket = useNewPdpDesignV2Bucket()
  const [showImagePopup, setShowImagePopup] = useState(false)
  const showNewPdpDesignV2 =
    newPdpDesignV2Bucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.NEW_PDP_DESIGN_V2].buckets?.control
  const isStyliticsBucket =
    newPdpDesignV2Bucket === STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.NEW_PDP_DESIGN_V2].buckets?.groupC
  const finalStyles = showNewPdpDesignV2 ? stylesV2 : styles
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  const classes = classnames(finalStyles.container, className, {
    [finalStyles.container_scrollingDown as string]: isScrollingDown,
    [finalStyles.container_addDivider as string]: isBundle,
    [finalStyles.container_pdpTagVisible as string]: isTagVisible,
    [finalStyles.container_isImagePopupOpen as string]: showImagePopup,
  })
  const showModelSizeTag = usePDPModelSizeTagExperiment()
  const tagsToShowOnPdp = productTags?.filter(tag => tag.showOnProductPage)
  const STYLE_WHITE = 'white'
  const STYLE_BLACK = 'black'
  const innerStyle = {
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
  }
  const isModelSizeVisibleOnMobile = showModelSizeTag && isMobile && mediaTags?.some(t => !!t)
  const productTagsNewClass = isModelSizeVisibleOnMobile && showNewPdpDesignV2

  const renderMainImageOverlay = useCallback(
    (index: number) => {
      const topLeftIconSVG = getTopLeftIconSVG?.({ index })
      const mediaTag = mediaTags?.[index] ? [mediaTags[index]] : []
      return (
        <div className={finalStyles.imageOverlay}>
          {topLeftIconSVG ? (
            <div className={finalStyles.awardIcon}>
              <Icon iconSvg={topLeftIconSVG} className={finalStyles.iconStyles} />
            </div>
          ) : null}
          {productTags && (isTagVisible || Boolean(tagsToShowOnPdp?.length)) && !isMobile && (
            <ProductTags
              applicableTags={isTagVisible && !isBundle ? productTags : tagsToShowOnPdp}
              position={isMobile ? TAG_POSITION.BOTTOM : TAG_POSITION.TOP}
              tagLocation={TAG_LOCATION.PDP}
            />
          )}
          {showModelSizeTag && !isMobile && mediaTag?.length ? (
            <ProductTags
              applicableTags={mediaTag}
              position={TAG_POSITION.BOTTOM}
              sticky={true}
              rounded={true}
              transformToSentenceCase={false}
              isModelSizeTag={true}
            />
          ) : null}
        </div>
      )
    },
    [productTags, getTopLeftIconSVG, isMobile, isTagVisible, showModelSizeTag, mediaTags],
  )

  const renderMediaInfoText = useCallback(
    (index: number) => {
      const mediaTag = mediaTags?.[index] ? [mediaTags[index]] : []
      const anyMediaHasModelSizeTags = mediaTags?.some(t => !!t)

      return showModelSizeTag && anyMediaHasModelSizeTags ? (
        <div className={classnames(styles.modelSizeInfo, showNewPdpDesignV2 && styles.modelSizeInfo_newDesignV2)}>
          {mediaTag[0]?.value}
        </div>
      ) : null
    },
    [mediaTags, showModelSizeTag],
  )

  const currentColor = selectedOptions?.find(opt => opt.name === ProductOptionName.Color)?.value ?? null
  const videoId = product ? getVideoInfoForColor(product, currentColor) : undefined
  const selectedVariant =
    product && selectedOptions?.length ? productHelper.getVariantsForOptions(product, selectedOptions)[0] : undefined
  const videoTrackingProperties = {
    productProperties: product
      ? getProductPropertiesForTracking(product, selectedVariant?.svid?.toString())
      : undefined,
    variant_id: selectedVariant?.svid ?? undefined,
    video_id: videoId ?? undefined,
  }

  return (
    <div style={{ position: 'relative' }}>
      <div className={classes}>
        <div
          className={classnames(
            finalStyles.container__header,
            breadCrumbContainerHeaderClass,
            finalStyles.container__header_adjustedPadding,
          )}
        >
          {!hideBreadCrumb && (
            <div className={finalStyles.container__header__text}>
              <BreadCrumb
                directory={directory}
                breadCrumbListClass={breadCrumbListClass}
                deviceType={deviceInfo.deviceType}
                showNewPdpDesignV2={showNewPdpDesignV2}
              />
            </div>
          )}
          {!hideWishList && !showNewPdpDesignV2 && (
            <Heart
              data-testid={
                isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON
              }
              style={innerStyle}
              className={finalStyles.container__header__icon}
              onClick={onWishListToggle}
            />
          )}
        </div>
        {(isTagVisible || Boolean(tagsToShowOnPdp?.length)) && isMobile && (
          <ProductTags
            applicableTags={isTagVisible && !isBundle ? productTags : tagsToShowOnPdp}
            position={isMobile ? TAG_POSITION.BOTTOM : TAG_POSITION.TOP}
            tagLocation={TAG_LOCATION.PDP}
            productTagsNewClass={productTagsNewClass}
            isModelSizeVisibleOnMobile={isModelSizeVisibleOnMobile}
          />
        )}

        <SliderImages
          {...sliderImageProps}
          renderMainImageOverlay={renderMainImageOverlay}
          variants={variants}
          product={product}
          videoTrackingProperties={videoTrackingProperties}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isWishListed={isWishListed}
          onWishListToggle={onWishListToggle}
          innerStyle={innerStyle}
          hideWishList={hideWishList}
          enableStyliticsJumplink={isStyliticsBucket && !DISABLE_PARTIAL_V2_DESIGN}
          renderMediaInfoText={renderMediaInfoText}
          isBundle={isBundle}
          showImagePopup={showImagePopup}
          setShowImagePopup={setShowImagePopup}
        />
      </div>
    </div>
  )
}

export default memo(SliderImageWithText)
